import { useEffect, useState } from 'react';
import invisaLogo from '@/assets/invisa-logo.jpg';
import siscomLogo from '@/assets/siscom-logo.jpg';

/**
 * Adaptive splash: shows for at least MIN_MS (brand moment), then dismisses
 * as soon as the minimum has elapsed. Caller decides when the app shell is
 * ready (AuthGate handles its own continuity skeleton), so we no longer
 * artificially block the UI for 1500–1900ms.
 */
export const SplashScreen = ({ onComplete }: { onComplete: () => void }) => {
  const [fadeOut, setFadeOut] = useState(false);
  const [logoScale, setLogoScale] = useState(0.95);

  const prefersReducedMotion = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const MIN_MS = prefersReducedMotion ? 250 : 600;
  const FADE_MS = prefersReducedMotion ? 150 : 250;

  useEffect(() => {
    const scaleTimer = setTimeout(() => setLogoScale(1), prefersReducedMotion ? 0 : 30);
    const fadeTimer = setTimeout(() => setFadeOut(true), MIN_MS);
    const completeTimer = setTimeout(() => onComplete(), MIN_MS + FADE_MS);

    return () => {
      clearTimeout(scaleTimer);
      clearTimeout(fadeTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete, prefersReducedMotion, MIN_MS, FADE_MS]);

  return (
    <div 
      className="fixed inset-0 z-[99999] bg-background flex items-center justify-center transition-opacity duration-400 ease-in-out"
      style={{ 
        opacity: fadeOut ? 0 : 1,
        pointerEvents: fadeOut ? 'none' : 'auto'
      }}
    >
      <div className="flex flex-col items-center gap-6">
        <img
          src={invisaLogo}
          alt="Invisa Logo"
          width={128}
          height={128}
          className="w-32 h-32 md:w-48 md:h-48 object-contain transition-transform duration-300 ease-out"
          style={{ transform: `scale(${logoScale})` }}
        />
        <div className="h-1 bg-gradient-to-r from-primary to-accent rounded-full w-[160px] animate-pulse" />
      </div>
      
      {/* Partner logo */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5">
        <span className="text-[10px] text-muted-foreground/70 font-light tracking-wide">
          Powered by
        </span>
        <img
          src={siscomLogo}
          alt="Siscom Africa"
          width={96}
          height={24}
          className="h-6 w-auto object-contain"
          style={{ imageRendering: 'crisp-edges' }}
        />
      </div>
    </div>
  );
};
